import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';

import { ApjTitleAbbreviation, ApjTitleEnum } from 'commons/enums';
import { PRIMARY_RED } from 'commons/styles/colors';
import { APJInfo, APJList, ICompanyInfo, OrderType } from 'commons/types';
import { CustomTabPanel } from 'pages/dashboard/commons/components';
import {
  uploadApjSignature,
  uploadApjStampedSignature,
} from 'redux-stores/actions/companyInfo.action';
import { RootReducerInterface } from 'redux-stores/reducers';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';

import S from '../CompanyInfo.styles';

interface ICompanyAPJInfoComponent {
  companyInfoState: ICompanyInfo | null;
  setCompanyInfoState: (c: ICompanyInfo) => void;
}

const CompanyAPJInfoComponent: React.FC<ICompanyAPJInfoComponent> = ({
  companyInfoState,
  setCompanyInfoState,
}) => {
  const { selectedLocation } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );
  const [tabValue, setTabValue] = useState<number>(0);

  const dispatch = useDispatch<AppDispatch>();

  const onFieldChangeAPJ = (
    apjType: keyof APJList,
    apjKey: keyof APJInfo,
    value: string,
  ) => {
    if (companyInfoState) {
      if (apjKey === 'title') {
        const enumKey = Object.keys(ApjTitleEnum).find(
          (k) => ApjTitleEnum[k as keyof typeof ApjTitleEnum] === value,
        ) as keyof typeof ApjTitleAbbreviation | undefined;

        setCompanyInfoState({
          ...companyInfoState,
          apj: {
            ...companyInfoState.apj,
            [apjType]: {
              ...companyInfoState.apj[apjType],
              title: value,
              abbreviation: enumKey
                ? ApjTitleAbbreviation[enumKey]
                : companyInfoState.apj[apjType].abbreviation,
            },
          },
        });
      } else {
        setCompanyInfoState({
          ...companyInfoState,
          apj: {
            ...companyInfoState.apj,
            [apjType]: {
              ...companyInfoState.apj[apjType],
              [apjKey]: value,
            },
          },
        });
      }
    }
  };

  const handleUploadPhoto = async (
    event: React.ChangeEvent<HTMLInputElement>,
    apjType: OrderType,
    isStamped: boolean,
  ): Promise<void> => {
    if (companyInfoState) {
      const file = event.target.files?.[0];
      if (file) {
        if (file.type === 'image/svg+xml') {
          if (isStamped) {
            await dispatch(
              uploadApjStampedSignature({
                companyInfo: companyInfoState,
                locationId: selectedLocation?.warehouse_id,
                apjType,
                file,
              }),
            );
          } else {
            await dispatch(
              uploadApjSignature({
                companyInfo: companyInfoState,
                locationId: selectedLocation?.warehouse_id,
                apjType,
                file,
              }),
            );
          }
        } else {
          dispatch(
            snackbarSetData({
              open: true,
              message: 'File type not allowed',
              color: PRIMARY_RED,
            }),
          );
        }
      }
    }
  };

  const renderAPJData = (key: OrderType, data: APJInfo | null) => (
    <S.InfoWrapper container style={{ flexDirection: 'column' }}>
      <Grid item xs={12}>
        <S.Label>Name</S.Label>
        <TextField
          variant="outlined"
          placeholder="-"
          value={data?.name || ''}
          id={`field-company-info-apj-${key}-name-${companyInfoState?.name}`}
          style={{ width: '100%' }}
          onChange={(event) =>
            onFieldChangeAPJ(key, 'name', event.target.value)
          }
        />
      </Grid>
      <Grid item xs={12}>
        <S.Label>Title</S.Label>
        <FormControl fullWidth>
          <Select
            value={data?.title || ''}
            id={`field-company-info-apj-${key}-title-${companyInfoState?.name}`}
            onChange={(event) =>
              onFieldChangeAPJ(key, 'title', event.target.value)
            }
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Title
            </MenuItem>
            {Object.values(ApjTitleEnum).map((title) => (
              <MenuItem key={title} value={title}>
                {title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <S.Label>Abbreviation</S.Label>
        <FormControl fullWidth>
          <Select
            value={data?.abbreviation || ''}
            id={`field-company-info-apj-${key}-abbreviation-${companyInfoState?.name}`}
            disabled
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Abbreviation
            </MenuItem>
            {Object.values(ApjTitleAbbreviation).map((abbr) => (
              <MenuItem key={abbr} value={abbr}>
                {abbr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <S.Label>SIPA</S.Label>
        <TextField
          variant="outlined"
          placeholder="-"
          value={data?.sipa || ''}
          id={`field-company-info-apj-${key}-sipa-${companyInfoState?.name}`}
          style={{ width: '100%' }}
          onChange={(event) =>
            onFieldChangeAPJ(key, 'sipa', event.target.value)
          }
        />
      </Grid>
      <Grid item xs={12}>
        <S.Label>Signature</S.Label>
        <S.ApjSignatureWrapper>
          {data?.signature ? (
            <img
              src={`${data?.signature}?${new Date()}`}
              alt={`apj ${key} signature`}
              style={{ width: '100%' }}
            />
          ) : (
            <S.Label>-</S.Label>
          )}
        </S.ApjSignatureWrapper>
        <div>
          <Button variant="outlined">
            <label htmlFor={`${key}-sign-input`}>
              <input
                id={`${key}-sign-input`}
                type="file"
                hidden
                onChange={(e) => handleUploadPhoto(e, key, false)}
                accept=".svg"
              />
              Upload
            </label>
          </Button>
        </div>
      </Grid>
      <Grid item xs={12}>
        <S.Label>Signature (with stamp)</S.Label>
        <S.ApjSignatureWrapper>
          {data?.stamped_signature ? (
            <img
              src={`${data?.stamped_signature}?${new Date()}`}
              alt={`apj ${key} signature with stamp`}
              style={{ width: '100%' }}
            />
          ) : (
            <S.Label>-</S.Label>
          )}
        </S.ApjSignatureWrapper>
        <div>
          <Button variant="outlined">
            <label htmlFor={`${key}-stamped-sign-input`}>
              <input
                id={`${key}-stamped-sign-input`}
                type="file"
                hidden
                onChange={(e) => handleUploadPhoto(e, key, true)}
                accept=".svg"
              />
              Upload
            </label>
          </Button>
        </div>
      </Grid>
    </S.InfoWrapper>
  );

  return (
    <S.APJInfoWrapper>
      <S.APJTitle>APJ Info</S.APJTitle>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newVal) => setTabValue(newVal)}>
          <Tab
            label={
              OrderType.REGULAR.toString().charAt(0).toUpperCase() +
              OrderType.REGULAR.toString().slice(1)
            }
          />
          <Tab
            label={
              OrderType.PRECURSOR.toString().charAt(0).toUpperCase() +
              OrderType.PRECURSOR.toString().slice(1)
            }
          />
          <Tab
            label={
              OrderType.ALKES.toString().charAt(0).toUpperCase() +
              OrderType.ALKES.toString().slice(1)
            }
          />
          <Tab
            label={
              OrderType.CCP.toString().charAt(0).toUpperCase() +
              OrderType.CCP.toString().slice(1)
            }
          />
          <Tab
            label={
              OrderType.PSIKOTROPIKA.toString().charAt(0).toUpperCase() +
              OrderType.PSIKOTROPIKA.toString().slice(1)
            }
          />
          <Tab label={OrderType.OOT.toString().toUpperCase()} />
        </Tabs>
      </Box>
      <CustomTabPanel value={tabValue} index={0}>
        {renderAPJData(
          OrderType.REGULAR,
          companyInfoState?.apj[OrderType.REGULAR] || null,
        )}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={1}>
        {renderAPJData(
          OrderType.PRECURSOR,
          companyInfoState?.apj[OrderType.PRECURSOR] || null,
        )}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={2}>
        {renderAPJData(
          OrderType.ALKES,
          companyInfoState?.apj[OrderType.ALKES] || null,
        )}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={3}>
        {renderAPJData(
          OrderType.CCP,
          companyInfoState?.apj[OrderType.CCP] || null,
        )}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={4}>
        {renderAPJData(
          OrderType.PSIKOTROPIKA,
          companyInfoState?.apj[OrderType.PSIKOTROPIKA] || null,
        )}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={5}>
        {renderAPJData(
          OrderType.OOT,
          companyInfoState?.apj[OrderType.OOT] || null,
        )}
      </CustomTabPanel>
    </S.APJInfoWrapper>
  );
};

export default CompanyAPJInfoComponent;
